import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { NgClass } from '@angular/common';
import { Component, Input, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { VERSAO_APP } from '@core/constants/versao';
import type { MenuItem, SubMenuItem } from '@core/layout/models/menu.model';
import { MenuService } from '@core/layout/services/menu.service';
import { IconComponent } from '@shared/components/icon/icon.component';
import { filter, map } from 'rxjs';

@Component({
  selector: 'app-menu-content',
  imports: [NgClass, RouterLink, IconComponent, OverlayModule],
  templateUrl: './menu-content.component.html',
  styleUrl: './menu-content.component.scss',
})
export class MenuContentComponent {
  menuService = inject(MenuService);
  private readonly router = inject(Router);

  /** Item sob o mouse na barra recolhida: é dele a dica com o nome. */
  protected readonly dica = signal<string | null>(null);

  /** Ao lado do ícone; se não couber à direita, cai para a esquerda. */
  protected readonly posicoesDica: ConnectedPosition[] = [
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 10 },
    { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -10 },
  ];

  /** Versão exibida no rodapé do menu — o app já mostra a dele no perfil. */
  protected readonly versao = VERSAO_APP;

  public readonly expanded = input(true);
  @Input() public isMobile = false;
  public readonly menuItemClicked = output<void>();

  protected readonly menu = toSignal(this.menuService.pagesMenu$, { initialValue: [] as MenuItem[] });

  /** URL atual sem query nem fragmento: é por ela que o menu decide quem acende. */
  private readonly urlAtual = toSignal(
    this.router.events.pipe(
      filter((evento): evento is NavigationEnd => evento instanceof NavigationEnd),
      map((evento) => this.semExtras(evento.urlAfterRedirects)),
      takeUntilDestroyed(),
    ),
    { initialValue: this.semExtras(this.router.url) },
  );

  /** Rotas das entradas de primeiro nível. */
  private readonly rotasDeTopo = computed(() => this.menu().flatMap((grupo) => grupo.items.map((item) => item.route)));

  trackByGroup(index: number, group: any): string {
    return group.group || index;
  }

  trackByItem(index: number, item: any): string {
    return item.route || index;
  }

  onMenuItemClick(): void {
    this.menuItemClicked.emit();
  }

  /**
   * Acende só a entrada mais específica. Por prefixo, `/fiscalizacao` (área do fiscal) casava também com
   * `/fiscalizacao/liberacoes` (qualidade) e as duas ficavam acesas. Rota que não é entrada do menu, como
   * `/usuarios/novo`, continua acendendo a entrada de onde saiu.
   */
  protected itemAtivo(rota?: string | null): boolean {
    return this.maisEspecifica(rota, this.rotasDeTopo());
  }

  /** Mesma regra entre os filhos de um item, que também são desenhados como links do menu. */
  protected filhoAtivo(rota: string | null | undefined, irmaos: SubMenuItem[] | undefined): boolean {
    return this.maisEspecifica(
      rota,
      (irmaos ?? []).map((irmao) => irmao.route),
    );
  }

  private maisEspecifica(rota: string | null | undefined, candidatas: (string | null | undefined)[]): boolean {
    if (!this.casa(rota)) return false;
    return !candidatas.some((outra) => outra !== rota && this.casa(outra) && outra!.length > rota!.length);
  }

  /** Casa a rota inteira ou um trecho dela: `/usuarios` casa `/usuarios/novo`, mas não `/usuarios-antigos`. */
  private casa(rota?: string | null): boolean {
    if (!rota) return false;
    const url = this.urlAtual();
    const raiz = rota.endsWith('/') ? rota.slice(0, -1) : rota;
    return url === raiz || url.startsWith(`${raiz}/`);
  }

  private semExtras(url: string): string {
    return url.split('?')[0].split('#')[0];
  }
}
